import type { MaybeRef } from '@vueuse/core';
import { get } from '@vueuse/core';
import orderBy from 'lodash/orderBy';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import type { ITournamentCenterEntranceMatch } from '@/interface/IMatch';
import { SportType } from '@/interface/enum';
import { remove, upsert } from '@/libraries/array';
import { deepCompareComputed } from '@/libraries/composables/deepCompareComputed';
import BusinessLogicHelper from '@/libraries/helpers/businessLogicHelper';
import { formatTournamentCenterEntranceEvent } from '@/libraries/oddsApi/oddsApiFormatter';
import { useFeatureToggleStore } from '@/stores/featureToggleStore';
import { useOddsApiStore } from '@/stores/oddsApiStore';
import { useRootStore } from '@/stores/rootStore';
import { EnumEventDateFilterType, EnumOddsCategoryType, EnumPresetFilterType, EnumTimeZoneType, useTournamentCenterEntranceEventsQuery, useTournamentCenterEntranceEventsSubscription } from '@/types';

interface UseTournamentCenterEntranceEventsProps {
    sportType: MaybeRef<SportType>;
    isMixParlayMode: MaybeRef<boolean>;
    tournamentIds?: MaybeRef<number[] | undefined>;
    tournamentNames?: MaybeRef<string[] | undefined>;
    isEnabled?: MaybeRef<boolean | undefined>;
    isSubScriptionEnabled?: MaybeRef<boolean | undefined>;
}

export function useTournamentCenterEntranceEvents({
    sportType,
    isMixParlayMode,
    tournamentIds = undefined,
    tournamentNames = undefined,
    isEnabled: _isEnabled = true,
    isSubScriptionEnabled = true,
}: UseTournamentCenterEntranceEventsProps) {
    const events = ref<ITournamentCenterEntranceMatch[]>([]);

    const variable = deepCompareComputed(() => ({
        query: {
            sport: SportType.toOddsApi(get(sportType)),
            filter: { presetFilter: EnumPresetFilterType.All, date: EnumEventDateFilterType.All },
            timeZone: EnumTimeZoneType.UTC_8,
            oddsCategory: get(isMixParlayMode) ? EnumOddsCategoryType.MixParlay : EnumOddsCategoryType.All,
            tournamentIds: get(tournamentIds),
            tournamentNames: get(tournamentNames),
        },
    }));

    const { customerSettings } = storeToRefs(useOddsApiStore());
    const isEnabled = computed(() => {
        if (!BusinessLogicHelper.isAllowedSport(customerSettings.value, SportType.toOddsApi(get(sportType)))) {
            return false;
        }
        return get(_isEnabled);
    });

    const { isWsConnected } = storeToRefs(useRootStore());

    const pollInterval = computed(() => {
        if (isWsConnected.value && get(isSubScriptionEnabled)) {
            return undefined;
        }
        return 30000;
    });

    const { onResult, loaded } = useTournamentCenterEntranceEventsQuery(
        variable,
        deepCompareComputed(() => ({
            enabled: isEnabled.value,
            pollInterval: get(pollInterval),
        })),
    );

    const { filterEFootballLeagueIds } = storeToRefs(useFeatureToggleStore());

    onResult((result) => {
        const filtered = result?.filter(event => BusinessLogicHelper.isEventAllowed(customerSettings.value, event, filterEFootballLeagueIds.value))
            .map(event => formatTournamentCenterEntranceEvent(event)) ?? [];
        events.value = sortMatches(filtered);
    });

    const { onSubscription } = useTournamentCenterEntranceEventsSubscription(
        variable,
        deepCompareComputed(() => ({
            enabled: isEnabled.value && get(isSubScriptionEnabled),
        })),
    );

    onSubscription((result) => {
        if (!result) {
            return;
        }

        upsert(events.value, result.updated
            .filter(event => BusinessLogicHelper.isEventAllowed(customerSettings.value, event, filterEFootballLeagueIds.value))
            .map(e => formatTournamentCenterEntranceEvent(e)), item => item.eventId);

        remove(events.value, result.deleted, (oldMatch, deletedMatch) => oldMatch.eventId === deletedMatch);

        events.value = sortMatches(events.value);
    });

    function sortMatches(matches: ITournamentCenterEntranceMatch[]) {
        return orderBy(matches, [x => x.isLive, x => x.eventCode], ['desc', 'asc']);
    }

    watch(variable, () => {
        events.value = [];
    });

    return {
        events,
        loaded,
    };
}
